import { useContext } from 'react';
import { Button, Modal } from 'react-bootstrap';
import styled, { useTheme } from 'styled-components';

import CloseButton from '../../../assets/svg/close-button.svg';
import { WELCOME_MODAL_PENDING_KEY, WELCOME_MODAL_SEEN_KEY } from '../../../constants/onboarding';
import { AppContext } from '../../../context/AppContext';
import { usePersistedState } from '../../../hooks';

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 646px;
  }
  .modal-content {
    overflow: hidden;
  }
  .close {
    display: none;
  }
`;

const Header = styled.div`
  position: relative;
  height: 290px;
  overflow: hidden;
  background: ${(props) => props.theme.images.welcomeModal.headerBackground};
`;

const Illustration = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledCloseButton = styled.button`
  position: absolute;
  right: 20px;
  top: 20px;
  padding: 10px;
  background: transparent;
  border: 0;
  cursor: pointer;
`;

/**
 * Shown once, the first time a user reaches the app after completing onboarding.
 * "Seen" state persists in localStorage so it never reappears for the same browser.
 */
const WelcomeModal = () => {
  const theme = useTheme();
  const { isOnboarded } = useContext(AppContext) as { isOnboarded: boolean };
  const [hasSeenWelcomeModal, setHasSeenWelcomeModal] = usePersistedState(WELCOME_MODAL_SEEN_KEY, false) as [
    boolean,
    (value: boolean) => void,
  ];
  const [hasPendingWelcome, setHasPendingWelcome] = usePersistedState(WELCOME_MODAL_PENDING_KEY, false) as [
    boolean,
    (value: boolean) => void,
  ];

  const onHide = () => {
    setHasSeenWelcomeModal(true);
    setHasPendingWelcome(false);
  };

  return (
    <StyledModal
      show={!!isOnboarded && !!hasPendingWelcome && !hasSeenWelcomeModal}
      onHide={onHide}
      aria-labelledby="welcome-modal-title"
      centered
    >
      <Header>
        {theme.images.welcomeModal.illustration && <Illustration src={theme.images.welcomeModal.illustration} alt="" />}
        <StyledCloseButton type="button" onClick={onHide} aria-label="Close">
          <img src={CloseButton} alt="" />
        </StyledCloseButton>
      </Header>
      <Modal.Body className="text-center px-4 pt-4 pb-4">
        <h1 id="welcome-modal-title" className="mb-3">
          Welcome
        </h1>
        <p className="mb-2">
          {theme.language.appName} is now successfully connected to your system, and will soon start saving you money.
        </p>
        <p className="mb-4">
          Please wait 30 minutes for the final calculations to be completed, then we&apos;ll start showing you your
          data.
        </p>
        <Button variant="primary" onClick={onHide} className="w-100">
          OK
        </Button>
      </Modal.Body>
    </StyledModal>
  );
};

export default WelcomeModal;
